import { useMemo } from 'react';

import { type LimitOrderFunders, useWalletTradeHistory } from '@/hooks/useWalletOpenLimitOrders';

import type { ChartPeriod, WalletChartPoint } from '@/utils/zerion/types';

import { tradeEntryToActivityMarker, type ActivityMarker } from './activityMarkers';
import { toMilliseconds } from './utils';

/** One request per lane covers the range; markers past this many are dropped. */
const MARKER_LIMIT = 1000;

/** How far back each chart period reaches; `max` starts at the chart's first point. */
const PERIOD_START_MS: Record<ChartPeriod, number | undefined> = {
  hour: 60 * 60 * 1000,
  day: 24 * 60 * 60 * 1000,
  week: 7 * 24 * 60 * 60 * 1000,
  month: 31 * 24 * 60 * 60 * 1000,
  year: 366 * 24 * 60 * 60 * 1000,
  max: undefined,
};

/** Rolling starts round to this, so the query key holds across renders and remounts. */
const START_ROUNDING_MS = 60 * 1000;

type UseActivityTradeMarkersParams = {
  funders: LimitOrderFunders;
  /** Narrows markers to these chains; all chains when unset. */
  chainIds?: string[];
  period: ChartPeriod;
  /** The value series the markers sit on. */
  chart: WalletChartPoint[];
  enabled: boolean;
};

/**
 * Every execution (swaps, bridges and advanced-order fills) to mark on the
 * portfolio value chart over the chart's range.
 */
export const useActivityTradeMarkers = ({
  funders,
  chainIds,
  period,
  chart,
  enabled,
}: UseActivityTradeMarkersParams) => {
  const maxStart = period === 'max' ? chart[0]?.timestamp : undefined;
  const startTimestamp = useMemo(() => {
    const span = PERIOD_START_MS[period];
    if (span) {
      const start = Math.floor((Date.now() - span) / START_ROUNDING_MS) * START_ROUNDING_MS;
      return new Date(start).toISOString();
    }
    // `max` has no lower bound, but the markers query needs one to page from.
    if (maxStart == null) return undefined;
    return new Date(toMilliseconds(maxStart)).toISOString();
  }, [period, maxStart]);

  const hasFunder = !!(funders.ethereum || funders.solana);
  const isActive = enabled && hasFunder && !!startTimestamp;

  // The history API serves swaps and advanced orders from separate lanes.
  const swaps = useWalletTradeHistory({
    funders,
    filters: { chainIds, orderTypes: 'swaps', startTimestamp },
    offset: 0,
    limit: MARKER_LIMIT,
    enabled: isActive,
  });
  const fills = useWalletTradeHistory({
    funders,
    // One marker per settled fill rather than one per order.
    filters: { chainIds, orderTypes: 'limit', expandLimitFills: true, startTimestamp },
    offset: 0,
    limit: MARKER_LIMIT,
    enabled: isActive,
  });

  const markers = useMemo<ActivityMarker[]>(() => {
    const byKey = new Map<string, ActivityMarker>();
    for (const entry of [...(swaps.data?.trades ?? []), ...(fills.data?.trades ?? [])]) {
      const marker = tradeEntryToActivityMarker(entry);
      if (marker && !byKey.has(marker.key)) byKey.set(marker.key, marker);
    }
    return [...byKey.values()];
  }, [swaps.data, fills.data]);

  return { markers };
};
